import StaticCookies from "assets/static/cookie.json";
import { PAGES_ALLOWED_BEFORE_COOKIE_CONSENT } from "common/config";
import { CookieUtil } from "common";
import { CookieType } from "common/enums";
import { WindowService } from "./window.service";

// cookiePolicy value format: www.ey.com/en_gl,180,performance^functional^;www.ey.com/en_in,180,performance^functional^
// We are supposed to update only "www.ey.com/en_gl" item.
class CookieConsentService {
  private static COOKIE_NAME = "cookiePolicy";

  private static COOKIE_DOMAIN = "www.ey.com/en_gl";

  private static COOKIE_ALL = "all";

  private static COOKIE_NONE = "none";

  private static COOKIE_TYPE_SEPARATOR = "^";

  private static COOKIE_TYPES_INDEX = 2;

  private static COOKIE_EXPIRY_DAYS = 365;

  private static NO_SELECTION_COOKIE_EXPIRY_DAYS = 90;

  /** This cookie holds the value of Do Not Track browser setting
     when the cookie consent or settings was last responded by the user. */
  private static DO_NOT_TRACK_COOKIE_NAME = "DNTState";

  private static DO_NOT_TRACK_COOKIE_EXPIRY_DAYS = 365;

  public static isCookieConsentReceived(): boolean {
    return this.getCookieItem() !== "";
  }

  public static isCookieAccepted(cookieType: CookieType): boolean {
    const cookieItem = this.getCookieItem();
    return (
      cookieItem.includes(cookieType) || cookieItem.includes(this.COOKIE_ALL)
    );
  }

  public static acceptAllCookies() {
    this.updateCookie([], true);
    this.saveDoNotTrackSettingCurrentValue();
  }

  public static declineOptionalCookies() {
    this.updateCookie([]);
    this.saveDoNotTrackSettingCurrentValue();
  }

  public static updateAcceptedCookies(acceptedCookieTypes: CookieType[]) {
    this.updateCookie(acceptedCookieTypes);
    this.saveDoNotTrackSettingCurrentValue();
    this.deleteDeclinedCookies(acceptedCookieTypes);
    WindowService.reloadApp();
  }

  // Returns the accepted cookie types which are saved in user browser.
  public static getAcceptedCookies(): CookieType[] {
    const acceptedCookieTypes: CookieType[] = [];
    const cookieItem = this.getCookieItem();

    if (cookieItem.includes(this.COOKIE_ALL)) {
      acceptedCookieTypes.push(CookieType.FUNCTIONAL);
      acceptedCookieTypes.push(CookieType.PERFORMANCE);
      acceptedCookieTypes.push(CookieType.ADVERTISING);
    } else {
      const parts = cookieItem.split(",");
      if (parts.length > this.COOKIE_TYPES_INDEX) {
        parts[this.COOKIE_TYPES_INDEX]
          .split(this.COOKIE_TYPE_SEPARATOR)
          .forEach((type) => {
            if (type.length > 0 && type !== this.COOKIE_NONE) {
              acceptedCookieTypes.push(type as CookieType);
            }
          });
      }
    }

    return acceptedCookieTypes;
  }

  public static deleteCookies(names: string[], domain = "") {
    names.forEach((name) => {
      CookieUtil.erase(name, domain);
    });
  }

  public static getParentDomain(): string {
    // cookiePolicy cookie should be set at parent domain level (i.e. ey.com)
    let domainName = ".ey.com";

    // localhost hack for developer box.
    if (window.location.hostname === "localhost") {
      domainName = window.location.hostname;
    }

    return domainName;
  }

  private static updateCookie(
    acceptedCookies: CookieType[] = [],
    allCookiesAccepted = false
  ) {
    let updatedValue = "";
    const enGlItem = this.composeCookieItem(
      acceptedCookies,
      allCookiesAccepted
    );
    const existingValue = CookieUtil.get(this.COOKIE_NAME);

    // Update only 'www.ey.com/en_gl' domain.
    if (existingValue) {
      const items = existingValue.split(";");
      const enGlItemIndex = items.findIndex((i: any) =>
        i.includes(this.COOKIE_DOMAIN)
      );
      if (enGlItemIndex > -1) {
        items[enGlItemIndex] = enGlItem;
      } else {
        items.push(enGlItem);
      }
      updatedValue = items.join(";");
    } else {
      updatedValue = enGlItem;
    }

    const isMandatoryOnly = !allCookiesAccepted && acceptedCookies.length == 0;

    const expiryIntervalSecs = this.convertToSeconds(
      isMandatoryOnly
        ? this.NO_SELECTION_COOKIE_EXPIRY_DAYS
        : this.COOKIE_EXPIRY_DAYS
    );

    CookieUtil.set(
      this.COOKIE_NAME,
      updatedValue,
      expiryIntervalSecs,
      this.getParentDomain(),
      true
    );
  }

  private static getCookieItem() {
    let result = "";
    const value = CookieUtil.get(this.COOKIE_NAME);

    if (value) {
      const domains = value.split(";");
      result = domains.find((d: any) => d.includes(this.COOKIE_DOMAIN)) || "";
    }

    return result;
  }

  private static convertToSeconds(days: number): number {
    return days * 86400; // 24 Hrs * 60 Mins * 60 Secs = 86400
  }

  private static composeCookieItem(
    acceptedCookies: CookieType[] = [],
    allCookiesAccepted = false
  ): string {
    let cookies = "";
    let cookieExpiryDays = this.COOKIE_EXPIRY_DAYS;
    if (allCookiesAccepted) {
      if (CookieConsentService.isDoNotTrackEnabled()) {
        cookies = [CookieType.FUNCTIONAL, CookieType.PERFORMANCE].join(
          this.COOKIE_TYPE_SEPARATOR
        );
      } else {
        cookies = this.COOKIE_ALL;
      }
    } else if (acceptedCookies.length === 0) {
      cookies = this.COOKIE_NONE;
      cookieExpiryDays = this.NO_SELECTION_COOKIE_EXPIRY_DAYS;
    } else {
      cookies = acceptedCookies.join(this.COOKIE_TYPE_SEPARATOR);
    }

    return `${this.COOKIE_DOMAIN},${cookieExpiryDays},${cookies}^`;
  }

  private static deleteDeclinedCookies(acceptedCookieTypes: CookieType[]) {
    [
      CookieType.FUNCTIONAL,
      CookieType.PERFORMANCE,
      CookieType.ADVERTISING
    ].forEach((type) => {
      if (!acceptedCookieTypes.includes(type)) {
        this.deleteCookiesByType(type);
      }
    });
  }

  private static deleteCookiesByType(cookieType: CookieType) {
    const cookieNames = StaticCookies[cookieType].map(
      (cookie: any) => cookie.name
    );
    const adobeAnalyticsCookies = [
      "AMCVS_1BA43D825A0C3C250A495E5C%40AdobeOrg",
      "AMCV_1BA43D825A0C3C250A495E5C%40AdobeOrg"
    ];

    // Add analytics cookies which have a random number in their name.
    // StaticText file has a placeholder for this random number, so they are added here explicitly.
    if (cookieType === CookieType.PERFORMANCE) {
      cookieNames.push(...adobeAnalyticsCookies);
    }

    this.deleteCookies(cookieNames);
    this.deleteCookies(cookieNames, this.getParentDomain()); // Delete cookies from parent domain as well.
  }

  public static isDoNotTrackEnabled = (): boolean => {
    let isEnabled = false;
    if (navigator.doNotTrack) {
      // The browser supports Do Not Track!
      if (navigator.doNotTrack == "yes" || navigator.doNotTrack == "1") {
        // Do Not Track is enabled!
        isEnabled = true;
      }
    }

    return isEnabled;
  };

  /** Returns true if the Do Not Track browser setting was changed by the user
     after they responded to cookie consent or cookie settings page. */
  public static isDoNotTrackSettingChanged = (): boolean => {
    const previousDoNotTrackValue = CookieUtil.get(
      CookieConsentService.DO_NOT_TRACK_COOKIE_NAME
    );
    const currentDoNotTrackValue =
      CookieConsentService.isDoNotTrackEnabled().toString();

    return previousDoNotTrackValue !== currentDoNotTrackValue;
  };

  private static saveDoNotTrackSettingCurrentValue = (): void => {
    const doNotTrackValue =
      CookieConsentService.isDoNotTrackEnabled().toString();
    const expiryIntervalSecs = CookieConsentService.convertToSeconds(
      CookieConsentService.DO_NOT_TRACK_COOKIE_EXPIRY_DAYS
    );
    CookieUtil.set(
      CookieConsentService.DO_NOT_TRACK_COOKIE_NAME,
      doNotTrackValue,
      expiryIntervalSecs,
      CookieConsentService.getParentDomain(),
      true
    );
  };

  public static canShowCookieConsentModal = () => {
    const path = window.location.pathname;
    const canCurrentPageAppearBeforeCookieConsent =
      PAGES_ALLOWED_BEFORE_COOKIE_CONSENT.includes(path);
    return (
      (!CookieConsentService.isCookieConsentReceived() ||
        CookieConsentService.isDoNotTrackSettingChanged()) &&
      !canCurrentPageAppearBeforeCookieConsent
    );
  };
}

export default CookieConsentService;
